import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  Sparkles,
  Loader2,
  Copy,
  CheckCircle2,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { CITATION_STYLES } from "@/lib/citation-styles";
import {
  generateCitation,
  generateFootnotes,
} from "@/lib/api/ai-writing.functions";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/education/writing/tools")({
  head: () => ({
    meta: [
      {
        title:
          "AI Citation & Footnote Tools — Legal Writing Academy | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Generate OSCOLA, Harvard, APA 7 and South African legal citations and footnotes from your own draft text. Educational tool — always verify against primary sources.",
      },
      { property: "og:title", content: "AI Citation & Footnote Tools" },
      {
        property: "og:description",
        content:
          "Citation and footnote assistance for OSCOLA, Harvard, APA 7 and South African legal citation.",
      },
    ],
  }),
  component: WritingTools,
});

type StyleSlug = (typeof CITATION_STYLES)[number]["slug"];
type SourceType =
  | "case"
  | "statute"
  | "journal_article"
  | "book"
  | "chapter"
  | "government_notice"
  | "website"
  | "other";

const SOURCE_TYPES: { value: SourceType; label: string }[] = [
  { value: "case", label: "Case law" },
  { value: "statute", label: "Statute / Act" },
  { value: "journal_article", label: "Journal article" },
  { value: "book", label: "Book" },
  { value: "chapter", label: "Book chapter" },
  { value: "government_notice", label: "Government Notice / Gazette" },
  { value: "website", label: "Website" },
  { value: "other", label: "Other" },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 rounded border border-input bg-card px-2.5 py-1 text-xs text-navy hover:border-gold"
    >
      {copied ? (
        <>
          <CheckCircle2 className="size-3 text-gold" /> Copied
        </>
      ) : (
        <>
          <Copy className="size-3" /> Copy
        </>
      )}
    </button>
  );
}

function useAuthed() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  if (authed === null && typeof window !== "undefined") {
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
    supabase.auth.onAuthStateChange((_e, session) => setAuthed(!!session?.user));
  }
  return authed;
}

function WritingTools() {
  const authed = useAuthed();
  const runCitation = useServerFn(generateCitation);
  const runFootnotes = useServerFn(generateFootnotes);

  // Citation generator state
  const [style, setStyle] = useState<StyleSlug>("oscola");
  const [sourceType, setSourceType] = useState<SourceType>("case");
  const [details, setDetails] = useState("");
  const [pinpoint, setPinpoint] = useState("");
  const [citationOutput, setCitationOutput] = useState<string | null>(null);
  const [citationError, setCitationError] = useState<string | null>(null);
  const [citationLoading, setCitationLoading] = useState(false);

  // Footnote generator state
  const [fnStyle, setFnStyle] = useState<StyleSlug>("oscola");
  const [passage, setPassage] = useState("");
  const [fnOutput, setFnOutput] = useState<string | null>(null);
  const [fnError, setFnError] = useState<string | null>(null);
  const [fnLoading, setFnLoading] = useState(false);

  const friendlyError = (msg: string) => {
    if (msg.includes("INSUFFICIENT_CREDITS"))
      return "You're out of credits. Top up from the Credits page to keep using the tool.";
    if (msg.includes("RATE_LIMIT"))
      return "The AI is busy right now. Please retry in a few seconds.";
    if (msg.includes("CREDITS_EXHAUSTED"))
      return "Workspace AI credits are temporarily exhausted. Please try again later.";
    return msg || "Something went wrong. Please try again.";
  };

  async function onGenerateCitation(e: React.FormEvent) {
    e.preventDefault();
    setCitationError(null);
    setCitationOutput(null);
    setCitationLoading(true);
    try {
      const result = await runCitation({
        data: { style, sourceType, details, pinpoint },
      });
      if (!result.ok) {
        setCitationError(result.error);
      } else {
        setCitationOutput(result.citation);
      }
    } catch (err) {
      setCitationError(friendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setCitationLoading(false);
    }
  }

  async function onGenerateFootnotes(e: React.FormEvent) {
    e.preventDefault();
    setFnError(null);
    setFnOutput(null);
    setFnLoading(true);
    try {
      const result = await runFootnotes({
        data: { style: fnStyle, passage },
      });
      if (!result.ok) {
        setFnError(result.error);
      } else {
        setFnOutput(result.output);
      }
    } catch (err) {
      setFnError(friendlyError(err instanceof Error ? err.message : ""));
    } finally {
      setFnLoading(false);
    }
  }

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-10 md:py-14">
          <Link
            to="/education/writing"
            className="inline-flex items-center gap-1 text-sm text-navy hover:underline"
          >
            <ArrowLeft className="size-4" /> Writing Academy
          </Link>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-gold">
            AI tools · 1 credit per generation
          </p>
          <h1 className="mt-1 font-display text-3xl text-navy md:text-4xl">
            Citation & Footnote Assistance
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            Paste the raw details of your source, pick a style, and the tool
            produces a clean citation or inserts numbered footnotes into your
            passage. Always verify every authority before submission.
          </p>
        </div>
      </section>

      {authed === false ? (
        <section className="container-narrow py-10">
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <h2 className="font-display text-xl text-navy">
              Sign in to use the AI tools
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              New accounts get 10 free credits. Each citation or footnote run
              uses 1 credit.
            </p>
            <Link
              to="/auth"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-medium text-white hover:bg-navy/90"
            >
              Sign in or register
            </Link>
          </div>
        </section>
      ) : null}

      <section className="container-narrow py-10">
        <div className="mb-3 flex items-center gap-2 text-navy">
          <Sparkles className="size-5 text-gold" />
          <h2 className="font-display text-2xl">Citation generator</h2>
        </div>
        <form
          onSubmit={onGenerateCitation}
          className="space-y-4 rounded-lg border border-border bg-card p-5"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as StyleSlug)}
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
              >
                {CITATION_STYLES.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.shortName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Source type
              </label>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as SourceType)}
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
              >
                {SOURCE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Source details
            </label>
            <textarea
              required
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={5}
              placeholder="e.g. Carmichele v Minister of Safety and Security, Constitutional Court, 2001, vol 4 SA 938, page 938"
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The more accurate detail you provide, the better the citation.
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Pinpoint (optional)
            </label>
            <input
              value={pinpoint}
              onChange={(e) => setPinpoint(e.target.value)}
              placeholder="e.g. para 44 / s 23(1) / p 253"
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <Button type="submit" disabled={citationLoading || !authed}>
            {citationLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="size-4" /> Generate citation (1 credit)
              </>
            )}
          </Button>

          {citationError ? (
            <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {citationError}
            </p>
          ) : null}
          {citationOutput ? (
            <div className="rounded border border-gold/40 bg-secondary/40 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-gold">
                  Citation
                </p>
                <CopyButton text={citationOutput} />
              </div>
              <p className="whitespace-pre-wrap font-mono text-sm text-navy">
                {citationOutput}
              </p>
            </div>
          ) : null}
        </form>
      </section>

      <section className="container-narrow py-10">
        <div className="mb-3 flex items-center gap-2 text-navy">
          <Sparkles className="size-5 text-gold" />
          <h2 className="font-display text-2xl">Footnote generator</h2>
        </div>
        <form
          onSubmit={onGenerateFootnotes}
          className="space-y-4 rounded-lg border border-border bg-card p-5"
        >
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Style
            </label>
            <select
              value={fnStyle}
              onChange={(e) => setFnStyle(e.target.value as StyleSlug)}
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm md:w-64"
            >
              {CITATION_STYLES.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.shortName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Paste a passage from your draft
            </label>
            <textarea
              required
              value={passage}
              onChange={(e) => setPassage(e.target.value)}
              rows={8}
              placeholder="Paste a paragraph or two. The tool will insert ^1, ^2 markers where authority is needed and produce a matching footnote list."
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Up to ~6,000 characters per run.
            </p>
          </div>
          <Button type="submit" disabled={fnLoading || !authed}>
            {fnLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="size-4" /> Generate footnotes (1 credit)
              </>
            )}
          </Button>
          {fnError ? (
            <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {fnError}
            </p>
          ) : null}
          {fnOutput ? (
            <div className="rounded border border-gold/40 bg-secondary/40 p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-gold">
                  Result
                </p>
                <CopyButton text={fnOutput} />
              </div>
              <pre className="whitespace-pre-wrap font-mono text-sm text-navy">
                {fnOutput}
              </pre>
            </div>
          ) : null}
        </form>
      </section>

      <section className="border-t border-border">
        <div className="container-narrow py-8 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-gold" />
            <span>
              AI-generated. Verify every citation against the primary source
              (gov.za, the official law report or the publisher's record). The
              AI may flag uncertain elements with{" "}
              <code className="rounded bg-secondary px-1">
                [REQUIRES VERIFICATION]
              </code>{" "}
              — never submit a citation containing that marker without checking
              it yourself.
            </span>
          </p>
        </div>
      </section>
    </>
  );
}
